import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BookOpenText,
  CheckCircle,
  ArrowRight,
  Spinner
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/course/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full"
      >
        {status === "checking" && (
          <div className="bg-white border-2 border-foreground rounded-3xl shadow-brutal p-8 text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Spinner size={40} className="text-primary animate-spin" />
            </div>
            <h1 className="font-fredoka font-bold text-2xl text-foreground mb-4">
              Processing Payment...
            </h1>
            <p className="font-nunito text-muted-foreground">
              Please wait while we confirm your payment.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-white border-2 border-foreground rounded-3xl shadow-brutal p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={50} weight="fill" className="text-green-500" />
            </div>
            <h1 className="font-fredoka font-bold text-2xl text-foreground mb-4">
              Welcome to the Course! 🎉
            </h1>
            <p className="font-nunito text-muted-foreground mb-6">
              Your payment was successful. You now have lifetime access to all course materials.
            </p>
            <p className="font-nunito text-sm text-muted-foreground mb-6">
              Check your email for access details.
            </p>
            <a href="/course/learn" data-testid="start-learning-btn">
              <Button className="w-full bg-primary hover:bg-primary-hover text-white font-nunito font-bold text-lg border-2 border-foreground shadow-brutal active-brutal rounded-xl py-6">
                Start Learning Now
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
            </a>
          </div>
        )}

        {status === "error" && (
          <div className="bg-white border-2 border-foreground rounded-3xl shadow-brutal p-8 text-center">
            <h1 className="font-fredoka font-bold text-2xl text-foreground mb-4">
              Something Went Wrong
            </h1>
            <p className="font-nunito text-muted-foreground mb-6">
              We couldn't verify your payment. Please contact support or check your email.
            </p>
            <a href="/course">
              <Button variant="outline" className="font-nunito font-bold border-2 border-foreground rounded-xl">
                Back to Course Page
              </Button>
            </a>
          </div>
        )}

        {status === "timeout" && (
          <div className="bg-white border-2 border-foreground rounded-3xl shadow-brutal p-8 text-center">
            <h1 className="font-fredoka font-bold text-2xl text-foreground mb-4">
              Verification Timeout
            </h1>
            <p className="font-nunito text-muted-foreground mb-6">
              Payment verification is taking longer than expected. Please check your email for confirmation.
            </p>
            <a href="/course/learn">
              <Button className="font-nunito font-bold border-2 border-foreground rounded-xl">
                Try Accessing Course
              </Button>
            </a>
          </div>
        )}

        <div className="mt-6 text-center">
          <a href="/" className="flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground">
            <BookOpenText size={20} />
            <span className="font-nunito">Embrace the Magic of Words</span>
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default CourseSuccess;
